<template>
  <div class="customerSummaryInfo">
    <div class="customerSummaryInfo__title">{{ $t('customer.summary.info.title') }}</div>
    <div v-if="loading" class="customerSummaryInfo__loading">
      <wa-spinner></wa-spinner>
    </div>
    <div v-else-if="error" class="customerSummaryInfo__error">
      {{ $t('customer.summary.load_error') }}
    </div>
    <template v-else>
      <div class="customerSummaryInfo__row">
        <span class="customerSummaryInfo__label">
          {{ $t('customer.summary.info.name_label') }}
        </span>
        <span>{{ info?.name ?? '–' }}</span>
      </div>
      <div class="customerSummaryInfo__row">
        <span class="customerSummaryInfo__label">
          {{ $t('customer.summary.info.age_label') }}
        </span>
        <span>{{ formattedAge }}</span>
      </div>
      <div class="customerSummaryInfo__row">
        <span class="customerSummaryInfo__label">
          {{ $t('customer.summary.info.orders_label') }}
        </span>
        <span>{{ formattedOrders }}</span>
      </div>
      <div class="customerSummaryInfo__row">
        <span class="customerSummaryInfo__label">
          {{ $t('customer.summary.info.first_order_label') }}
        </span>
        <span>{{ formattedOldestOrder }}</span>
      </div>
      <div v-if="statusLabel || info?.frequentReturns" class="customerSummaryInfo__badges">
        <wa-tag v-if="statusLabel" :variant="statusVariant" size="s">{{ statusLabel }}</wa-tag>
        <wa-tag
          v-if="info?.frequentReturns"
          class="customerSummaryInfo__returnsBadge"
          variant="primary-danger"
          size="s"
        >
          {{ $t('customer.summary.info.frequent_returns_badge') }}
        </wa-tag>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatISODateToDDMMYYYY } from '@/utils/dateTime.js'

const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  }
})

const { t } = useI18n()

const formattedAge = computed(() => {
  const age = Number.isFinite(props.info?.age) ? props.info.age : '–'
  return props.info?.gender ? `${age} · ${t(`genders.${props.info.gender}`)}` : `${age}`
})

const formattedOrders = computed(() =>
  Number.isFinite(props.info?.totalOrders) ? props.info.totalOrders.toLocaleString('de-CH') : '–'
)

const formattedOldestOrder = computed(() =>
  props.info?.oldestOrderDate ? formatISODateToDDMMYYYY(props.info.oldestOrderDate) : '–'
)

// Solid variants from the shared wa-tag set, so the card needs no colours of its own.
const STATUS_VARIANTS = {
  repeat: 'primary-green',
  acquired: 'primary-blue',
  prospect: 'primary-neutral'
}

const statusLabel = computed(() =>
  props.info?.status ? t(`cl_customer_status.${props.info.status}.label`) : null
)

const statusVariant = computed(() => STATUS_VARIANTS[props.info?.status] ?? 'primary-neutral')
</script>

<style scoped>
.customerSummaryInfo {
  &__title {
    font-size: var(--wa-font-size-xs);
    color: var(--wa-color-neutral-50);
    margin-bottom: 8px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    font-size: var(--wa-font-size-s);
    padding: 2px 0;
  }

  &__label {
    color: var(--wa-color-neutral-50);
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
  }

  &__returnsBadge {
    margin-left: auto;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }

  &__error {
    font-size: var(--wa-font-size-s);
    color: var(--wa-color-neutral-50);
  }
}
</style>
